import { StaticConfig } from '@constants';
import createStoreAsync from '@state/index.ts';
import { setDomLoaded, setShowPageLoader } from '@state/reducers/config.ts';
import { errorHandler, getQueryAffiliates } from '@utils/index.ts';
import {
  injectFullstoryScript,
  injectLeverageMediaScript,
  injectWagerGamesScript,
  injectZendeskScript,
} from '@utils/uiUtils.ts';
import React from 'react';
import { createRoot } from 'react-dom/client';
import ApiHead from '../ApiHead.tsx';
import ContextProviders from '../ContextProviders.tsx';
import StateProvider from '../StateProvider.tsx';
import { GeoComplyProvider } from '../geo-comply-provider.tsx';
import { SocketProvider } from '../websocket-provider.tsx';
import { App } from './App.tsx';
import { XtremePushScript } from './components/xtreme-push-script.tsx';

globalThis.addEventListener('error', errorHandler);

const MOUNT_NODE: HTMLElement | null = document.getElementById('root');
if (!MOUNT_NODE) {
  throw new Error(
    'Failed to find an element with an id of "root" to mount the application.',
  );
}
const root = createRoot(MOUNT_NODE);

getQueryAffiliates();

Promise.all([
  createStoreAsync().then(store => store),
  new Promise(resolve => {
    if (globalThis.document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', resolve);
    } else {
      // DOM is already loaded
      resolve(null);
    }
  }),
])
  .then(([store]) => {
    // NOTE(2025-07-27): This sets the `data-theme` attribute on the `<html>`
    // element to the franchise name. This allows the CSS custom properties in
    // the client-base.css file in the `public` directory to be scoped to a
    // specific franchise.
    //
    // Ideally this would be done statically in the `public/index.html` file,
    // but because the franchise can be changed dynamically at runtime during
    // development, it needs to dynamically injected.
    document.documentElement.dataset.theme = StaticConfig.name;

    store.dispatch(setDomLoaded());

    if (StaticConfig.zendesk) injectZendeskScript();
    if (StaticConfig.wagerGames) injectWagerGamesScript();
    if (StaticConfig.fullStory) injectFullstoryScript();
    if (StaticConfig.leverageMedia) injectLeverageMediaScript();

    root.render(
      <StateProvider store={store}>
        <ContextProviders>
          <GeoComplyProvider>
            <SocketProvider>
              <ApiHead />
              <App />
              <XtremePushScript />
            </SocketProvider>
          </GeoComplyProvider>
        </ContextProviders>
      </StateProvider>,
    );

    store.dispatch(setShowPageLoader(false));
  })
  .catch(error => {
    console.error(error);
    console.trace(error);
    throw error;
  });
